<template>
  <page-header-wrapper>
    <div class="business-page erp-list-page baidao-list-page purchase-reference-summary-page">
      <section class="erp-search-card">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="8" :sm="24">
              <a-form-item label="日期">
                <a-range-picker v-model="dateRange" style="width: 100%" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="出库单号">
                <a-input v-model="query.outboundNo" allow-clear placeholder="销售出库单号" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="客商">
                <a-input v-model="query.customerKeyword" allow-clear placeholder="客商编号或名称" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="供应商">
                <a-select
                  v-model="query.supplierId"
                  allow-clear
                  show-search
                  option-filter-prop="children"
                  placeholder="全部供应商">
                  <a-select-option v-for="item in supplierOptions" :key="item.id" :value="item.id">
                    {{ item.supplierCode }} · {{ item.supplierName }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="分类">
                <a-tree-select
                  v-model="query.categoryId"
                  allow-clear
                  show-search
                  tree-node-filter-prop="title"
                  :dropdown-style="{ maxHeight: '360px', overflow: 'auto' }"
                  placeholder="全部分类"
                  :tree-data="categoryTreeSelect"
                  style="width: 100%"
                />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="规格">
                <a-input v-model="query.specKeyword" allow-clear placeholder="SKU编码或规格名称" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="单位">
                <a-select v-model="query.unitType" @change="onUnitTypeChange">
                  <a-select-option value="business">销售单位</a-select-option>
                  <a-select-option value="base">基本单位</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="库存设置">
                <a-select v-model="query.inventorySetting" allow-clear placeholder="全部" @change="search">
                  <a-select-option v-for="o in inventorySettingOptions" :key="o" :value="o">{{ o }}</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="24" :sm="24" class="erp-search-actions">
              <a-button type="primary" icon="search" @click="search">查询</a-button>
              <a-button icon="redo" @click="resetQuery">重置</a-button>
            </a-col>
          </a-row>
        </a-form>
      </section>

      <section class="erp-table-card">
        <div class="erp-table-toolbar">
          <div>
            <h3>采购参考汇总表</h3>
            <p>在商品销售汇总口径上，按库存设置、供应商与 SKU 汇总；查询条件与采购参考明细一致。销售金额为价税合计减折让；实际销售=销售−退货；实际总销量=实际销售数量+赠品数量。</p>
          </div>
          <div class="erp-toolbar-actions">
            <span v-if="pagination.total" class="purchase-reference-summary-totals">
              实际销售 <strong class="erp-money">¥{{ formatMoney(actualSalesTotal) }}</strong>
              成本 <strong class="erp-money">¥{{ formatMoney(purchaseCostTotal) }}</strong>
              毛利 <strong class="erp-money">¥{{ formatMoney(grossProfitTotal) }}</strong>
            </span>
            <a-button
              v-hasPermi="['erp:report:purchase-reference-summary:export']"
              icon="download"
              :loading="exporting"
              :disabled="!pagination.total"
              @click="doExport">导出</a-button>
          </div>
        </div>
        <a-table
          :key="tableRenderKey"
          row-key="rowKey"
          bordered
          size="middle"
          :columns="tableColumns"
          :data-source="rows"
          :loading="loading"
          :pagination="pagination"
          :scroll="tableScroll"
          :locale="{ emptyText: '暂无汇总数据' }"
          @change="onPageChange">
          <template slot="text" slot-scope="value">
            <span :title="displayValue(value)">{{ displayValue(value) }}</span>
          </template>
          <template slot="qty" slot-scope="text">{{ formatQty(text) }}</template>
          <template slot="money" slot-scope="value"><span class="erp-money">¥{{ formatMoney(value) }}</span></template>
          <template slot="rate" slot-scope="value">{{ formatRate(value) }}</template>
        </a-table>
      </section>
    </div>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import {
  pagePurchaseReferenceSummaryReport,
  listPurchaseReferenceSummarySupplierOptions,
  exportPurchaseReferenceSummaryReport
} from '@/api/erp/purchaseReferenceSummaryReport'
import { treeProductCategory } from '@/api/erp/productCategory'

const emptyQuery = () => ({
  outboundNo: '',
  customerKeyword: '',
  supplierId: undefined,
  categoryId: undefined,
  specKeyword: '',
  unitType: 'business',
  inventorySetting: undefined
})

export default {
  name: 'ErpPurchaseReferenceSummaryReport',
  data () {
    return {
      query: emptyQuery(),
      dateRange: [],
      categoryTreeRaw: [],
      supplierOptions: [],
      inventorySettingOptions: ['虚拟库存', '实际库存', '设置库存'],
      rows: [],
      actualSalesTotal: 0,
      purchaseCostTotal: 0,
      grossProfitTotal: 0,
      loading: false,
      exporting: false,
      tableRenderKey: 0,
      pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true, showTotal: total => `共 ${total} 条` }
    }
  },
  computed: {
    categoryTreeSelect () {
      return this.toTreeSelectData(this.categoryTreeRaw)
    },
    tableScroll () {
      const w = this.tableColumns.reduce((sum, col) => sum + (col.width || 0), 0)
      return w > 0 ? { x: w } : undefined
    },
    tableColumns () {
      return [
        { title: '库存设置', dataIndex: 'inventorySetting', width: 96, fixed: 'left', scopedSlots: { customRender: 'text' } },
        { title: '供应商', dataIndex: 'supplierName', width: 120, fixed: 'left', scopedSlots: { customRender: 'text' } },
        { title: 'SKU编码', dataIndex: 'skuCode', width: 120, fixed: 'left', ellipsis: true, scopedSlots: { customRender: 'text' } },
        { title: 'SKU名称', dataIndex: 'skuName', width: 140, scopedSlots: { customRender: 'text' } },
        { title: '规格名称', dataIndex: 'specName', width: 110, scopedSlots: { customRender: 'text' } },
        { title: '销售数量', dataIndex: 'displaySaleQty', width: 96, scopedSlots: { customRender: 'qty' } },
        { title: '销售单位', dataIndex: 'displaySaleUnitName', width: 88, scopedSlots: { customRender: 'text' } },
        { title: '销售单价', dataIndex: 'displaySaleUnitPrice', width: 96, scopedSlots: { customRender: 'money' } },
        { title: '合计销售金额', dataIndex: 'displaySaleAmount', width: 112, scopedSlots: { customRender: 'money' } },
        { title: '退货数量', dataIndex: 'displayReturnQty', width: 96, scopedSlots: { customRender: 'qty' } },
        { title: '退货单位', dataIndex: 'displayReturnUnitName', width: 88, scopedSlots: { customRender: 'text' } },
        { title: '退货单价', dataIndex: 'displayReturnUnitPrice', width: 96, scopedSlots: { customRender: 'money' } },
        { title: '退货金额', dataIndex: 'displayReturnAmount', width: 96, scopedSlots: { customRender: 'money' } },
        { title: '实际销售数量', dataIndex: 'displayActualQty', width: 108, scopedSlots: { customRender: 'qty' } },
        { title: '实际销售单位', dataIndex: 'displayActualUnitName', width: 100, scopedSlots: { customRender: 'text' } },
        { title: '实际销售金额', dataIndex: 'displayActualAmount', width: 112, scopedSlots: { customRender: 'money' } },
        { title: '赠品数量', dataIndex: 'displayGiftQty', width: 96, scopedSlots: { customRender: 'qty' } },
        { title: '实际总销量', dataIndex: 'displayTotalQty', width: 100, scopedSlots: { customRender: 'qty' } },
        { title: '实际采购成本', dataIndex: 'displayPurchaseCost', width: 112, scopedSlots: { customRender: 'money' } },
        { title: '毛利额', dataIndex: 'displayGrossProfit', width: 96, scopedSlots: { customRender: 'money' } },
        { title: '毛利率(%)', dataIndex: 'displayGrossProfitRate', width: 96, scopedSlots: { customRender: 'rate' } }
      ]
    }
  },
  created () {
    treeProductCategory().then(res => {
      this.categoryTreeRaw = this.normalizeTree((res && res.data) || [])
    }).catch(() => { this.categoryTreeRaw = [] })
    listPurchaseReferenceSummarySupplierOptions().then(res => {
      this.supplierOptions = res.data || []
    }).catch(() => { this.supplierOptions = [] })
    this.load()
  },
  methods: {
    normalizeTree (nodes) {
      return (nodes || []).map(n => ({
        id: n.id,
        categoryName: n.categoryName || n.name,
        children: this.normalizeTree(n.children || [])
      }))
    },
    toTreeSelectData (nodes) {
      return (nodes || []).map(n => ({
        title: n.categoryName,
        value: n.id,
        key: String(n.id),
        children: n.children && n.children.length ? this.toTreeSelectData(n.children) : undefined
      }))
    },
    displayValue (value) {
      return value === null || value === undefined || value === '' ? '—' : value
    },
    formatQty (value) {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      if (Number.isNaN(n)) return '—'
      const s = n.toFixed(6)
      return s.replace(/\.?0+$/, '') || '0'
    },
    formatMoney (value) {
      return Number(value || 0).toFixed(2)
    },
    formatRate (value) {
      if (value === null || value === undefined || value === '') return '—'
      return Number(value).toFixed(2)
    },
    queryParams () {
      const specKeyword = (this.query.specKeyword || '').trim()
      const outboundNo = (this.query.outboundNo || '').trim()
      const customerKeyword = (this.query.customerKeyword || '').trim()
      return {
        outboundNo: outboundNo || undefined,
        customerKeyword: customerKeyword || undefined,
        supplierId: this.query.supplierId,
        categoryId: this.query.categoryId,
        specKeyword: specKeyword || undefined,
        inventorySetting: this.query.inventorySetting || undefined,
        dateStart: this.dateRange[0] ? this.dateRange[0].format('YYYY-MM-DD') : undefined,
        dateEnd: this.dateRange[1] ? this.dateRange[1].format('YYYY-MM-DD') : undefined,
        unitType: this.query.unitType || 'business',
        pageNum: this.pagination.current,
        pageSize: this.pagination.pageSize
      }
    },
    load () {
      this.loading = true
      pagePurchaseReferenceSummaryReport(this.queryParams()).then(res => {
        const data = res.data || {}
        this.rows = (data.records || []).map(r => ({ ...r }))
        this.tableRenderKey += 1
        this.pagination.total = Number(data.total || 0)
        this.actualSalesTotal = Number(data.actualSalesTotal || 0)
        this.purchaseCostTotal = Number(data.purchaseCostTotal || 0)
        this.grossProfitTotal = Number(data.grossProfitTotal || 0)
      }).catch(() => {
        this.rows = []
        this.pagination.total = 0
      }).finally(() => { this.loading = false })
    },
    search () {
      this.pagination.current = 1
      this.load()
    },
    onUnitTypeChange (val) {
      this.query.unitType = val || 'business'
      this.search()
    },
    resetQuery () {
      this.query = emptyQuery()
      this.dateRange = []
      this.pagination.current = 1
      this.load()
    },
    onPageChange (pag) {
      this.pagination.current = pag.current
      this.pagination.pageSize = pag.pageSize
      this.load()
    },
    async doExport () {
      this.exporting = true
      try {
        const params = { ...this.queryParams() }
        delete params.pageNum
        delete params.pageSize
        const res = await exportPurchaseReferenceSummaryReport(params)
        const blob = res instanceof Blob
          ? res
          : new Blob([res && res.data !== undefined ? res.data : res], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `采购参考汇总_${moment().format('YYYYMMDDHHmmss')}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.purchase-reference-summary-totals {
  margin-right: 12px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
}
.purchase-reference-summary-totals strong {
  margin: 0 8px 0 4px;
}
</style>
